import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'jiaoyou');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeCategory),
    [activeCategory]
  );

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeCategory),
    [activeCategory]
  );

  const getCategoryName = (catSlug: string) => {
    const cat = categories.find((c) => c.slug === catSlug);
    return cat ? cat.name : catSlug;
  };

  const webpageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || ''} - 男同网站`,
    description: currentCategory?.description || '',
    url: `https://www.nantongwangzhan.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '男同网站',
      url: 'https://www.nantongwangzhan.com',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Article',
          name: article.title,
          url: `https://www.nantongwangzhan.com/article/${article.slug}`,
        },
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(webpageJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <section className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory?.name || '' },
                ]}
              />
              <div className="flex items-center gap-4 mt-2">
                <div className="w-14 h-14 bg-primary-100 rounded-lg flex items-center justify-center">
                  <i className={`${currentCategory?.icon || 'ri-folder-line'} text-2xl text-primary-600`}></i>
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                    {currentCategory?.name}
                  </h1>
                  <p className="text-foreground-600 text-sm mt-1">
                    {currentCategory?.description}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 py-3 overflow-x-auto flex-wrap">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredArticles.map((article) => (
                    <ArticleCard
                      key={article.id}
                      id={article.id}
                      title={article.title}
                      slug={article.slug}
                      summary={article.summary}
                      category={article.category}
                      categoryName={getCategoryName(article.category)}
                      coverImage={article.coverImage}
                      author={article.author}
                      publishedAt={article.publishedAt}
                      readTime={article.readTime}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-base">该分类暂无文章</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}